import type { TodoData, Context } from "../../../types";
import { nextStepOrder } from "./stepOrder";

// A sharee's optimistic writes to one shared project: add a step, check or
// uncheck it, rename it. Same approach as DataProvider: apply locally, call
// the API, and on failure undo only what this call changed. Overlapping edits
// to one field settle at the newest edit that succeeded, or the original value
// if none did. No polling, no retries.

type StepApi = {
  addStep: (input: {
    id: string;
    title: string;
    context: Context;
    projectId: string;
    projectCreatedAt: string;
  }) => Promise<TodoData>;
  updateStep: (input: {
    id: string;
    createdAt: string;
    projectId: string;
    updates: { title?: string; done?: boolean };
  }) => Promise<unknown>;
};

export type SharedStepsDeps = {
  api: StepApi;
  projectId: string;
  projectCreatedAt: string;
  context: Context;
  mutate: (fn: (steps: TodoData[]) => TodoData[]) => void;
  getSteps: () => TodoData[];
  onError: (message: string) => void;
  // The server refused a write (403/404): the share is gone or not writable.
  onBlocked: (message: string) => void;
};

type FieldEdit = { base: unknown; inflight: number; seq: number; okSeq: number; okValue: unknown };

const BLOCKED = "You can no longer edit this project. It may have been unshared or deleted.";
const isBlocked = (e: unknown) => {
  const status = (e as { status?: unknown } | null)?.status;
  return status === 403 || status === 404;
};

export const createSharedSteps = (deps: SharedStepsDeps) => {
  const { api, projectId, projectCreatedAt, context, mutate, getSteps, onError, onBlocked } = deps;
  const edits = new Map<string, FieldEdit>();

  // A refused write hides the controls (via onBlocked); anything else is
  // reported as a plain failure.
  const fail = (e: unknown, message: string) => {
    if (isBlocked(e)) onBlocked(BLOCKED);
    else onError(message);
  };

  const addStep = async (rawTitle: string) => {
    const title = rawTitle.trim();
    if (!title) return;
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    // createdAt stays empty until the server answers; it is part of the key
    // needed to edit the step, so the UI holds edits until it is set.
    const optimistic: TodoData = {
      type: "todo",
      id,
      userId: "",
      title,
      context,
      parentId: projectId,
      done: false,
      order: nextStepOrder(), // bottom of the steps, matching the server
      timesScheduled: 0,
      createdAt: "",
      updatedAt: now,
    };
    mutate((s) => [...s, optimistic]);
    try {
      const saved = await api.addStep({ id, title, context, projectId, projectCreatedAt });
      mutate((s) => s.map((x) => (x.id === id ? saved : x)));
    } catch (e) {
      mutate((s) => s.filter((x) => x.id !== id));
      fail(e, "Couldn't add that step, so it was removed.");
    }
  };

  const edit = async (step: TodoData, updates: { title?: string; done?: boolean }) => {
    const current = getSteps().find((x) => x.id === step.id);
    if (!current || !current.createdAt) return; // gone, or still being created
    const keys = Object.keys(updates) as ("title" | "done")[];
    const mine = keys.map((key) => {
      const mapKey = `${step.id}:${key}`;
      let e = edits.get(mapKey);
      if (!e) {
        e = { base: current[key], inflight: 0, seq: 0, okSeq: 0, okValue: undefined };
        edits.set(mapKey, e);
      }
      e.inflight++;
      return { key, mapKey, e, seq: ++e.seq };
    });
    mutate((s) => s.map((x) => (x.id === step.id ? { ...x, ...updates } : x)));
    let ok = false;
    let error: unknown;
    try {
      await api.updateStep({ id: step.id, createdAt: current.createdAt, projectId, updates });
      ok = true;
    } catch (e) {
      error = e;
    }
    const revert: Partial<Pick<TodoData, "title" | "done">> = {};
    for (const { key, mapKey, e, seq } of mine) {
      e.inflight--;
      if (ok && seq > e.okSeq) {
        e.okSeq = seq;
        e.okValue = updates[key];
      }
      if (e.inflight > 0) continue;
      edits.delete(mapKey);
      if (e.okSeq !== e.seq) (revert as Record<string, unknown>)[key] = e.okSeq ? e.okValue : e.base;
    }
    const now = getSteps().find((x) => x.id === step.id);
    const reverted =
      !!now && (Object.keys(revert) as (keyof typeof revert)[]).some((k) => now[k] !== revert[k]);
    if (reverted) mutate((s) => s.map((x) => (x.id === step.id ? { ...x, ...revert } : x)));
    if (!ok && (reverted || isBlocked(error))) fail(error, "Couldn't save that change, so it was undone.");
  };

  return {
    addStep,
    setDone: (step: TodoData, done: boolean) => edit(step, { done }),
    rename: (step: TodoData, rawTitle: string) => {
      const title = rawTitle.trim();
      if (!title || title === step.title) return Promise.resolve();
      return edit(step, { title });
    },
  };
};
